/**
 * Resolve the URL slug for a team. Teams from `$utils/getTeams` arrive
 * with `path` already decorated (issue #37); we just stitch in the base
 * path via SvelteKit's `resolve(...)`.
 *
 * When the global `appState.embed` flag is set (i.e. we're rendering
 * inside the `/embeds/*` layout, which lives in a third-party iframe),
 * URLs are instead anchored on the package `homepage` so they always
 * point back at the live production page rather than at a SvelteKit
 * relative URL that would resolve against the host page.
 */

import { appState } from '$lib/stores/index.svelte';
import { homepage } from '$pkg';
import { resolve } from '$app/paths';

/**
 * @typedef {Object} TeamPath
 * @property {string} label - Display label for the team (the team's name).
 * @property {string} url - URL path to the team's page.
 */

/**
 * Returns `{ label, url }` for a team page. Falls back to deriving the
 * URL from `team.slug` for synthetic team objects that don't go through
 * the data pipeline.
 *
 * @param {{ name?: string, slug?: string, path?: string } | null | undefined} team
 * @returns {TeamPath | undefined}
 */
export const getTeamPath = (team) => {
  const path = team?.path ?? (team?.slug ? `teams/${team.slug}/` : undefined);
  if (!path) return;

  if (appState.embed) {
    const base = homepage.replace(/\/$/, '');
    const tail = String(path).replace(/^\//, '');
    return {
      label: team?.name ?? '',
      url: `${base}/${tail}`,
    };
  }

  return {
    label: team?.name ?? '',
    url: resolve(`/${path}`),
  };
};

export default getTeamPath;
